/**
 * Version History Component
 * Displays and manages version snapshots of reports
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { History, Save, Eye, Clock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Version {
  id: string;
  report_id: string;
  version_number: number;
  created_by: string;
  created_by_type: 'user' | 'accountant';
  created_by_name: string;
  created_at: string;
  change_summary: string;
}

interface VersionHistoryProps {
  reportId: string;
  getAuthHeader: () => any;
}

export default function VersionHistory({ reportId, getAuthHeader }: VersionHistoryProps) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [reportId]);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/versions`,
        { headers: getAuthHeader() }
      );
      setVersions(response.data);
    } catch (err: any) {
      console.error('Failed to fetch versions:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeSummary.trim()) return;

    setCreating(true);
    setError('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/versions`,
        {
          report_id: reportId,
          change_summary: changeSummary.trim()
        },
        { headers: getAuthHeader() }
      );

      setVersions([response.data, ...versions]);
      setChangeSummary('');
      setShowCreateModal(false);
    } catch (err: any) {
      console.error('Failed to create version:', err);
      setError(err.response?.data?.detail || 'Failed to create version');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Version History</h3>
          {versions.length > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              {versions.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
        >
          <Save className="h-4 w-4" />
          Create Snapshot
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {/* Version List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-gray-500 text-sm">Loading version history...</div>
        ) : versions.length === 0 ? (
          <div className="text-gray-500 text-sm italic text-center py-8">
            No versions yet. Create a snapshot to track changes.
          </div>
        ) : (
          versions.map((version, index) => (
            <div
              key={version.id}
              className={`border rounded-lg p-4 ${
                index === 0 ? 'border-green-300 bg-green-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-gray-900">
                      Version {version.version_number}
                    </span>
                    {index === 0 && (
                      <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>

                  <p className="text-gray-700 text-sm mb-3">{version.change_summary}</p>

                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{version.created_by_name}</span>
                      <span className={`ml-1 px-2 py-0.5 rounded ${
                        version.created_by_type === 'accountant'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {version.created_by_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(version.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Version Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create Version Snapshot
            </h3>

            <form onSubmit={handleCreateVersion}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What changed in this version?
                </label>
                <textarea
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  placeholder="e.g., Updated calculation methodology based on new evidence"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setChangeSummary('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !changeSummary.trim()}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Snapshot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
